import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import ProgressStats from '@/components/golfer/ProgressStats';
import LastRunCard from '@/components/golfer/LastRunCard';
import {
  getGolferProgress,
  type GolferProgress,
} from '@/lib/golfer-dashboard';
import { useAuth } from '@/hooks/use-auth';

export default function GolferDashboard() {
  const { user } = useAuth();
  const golferId = user?.id ?? null;

  const [progress, setProgress] = useState<GolferProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!golferId) {
          // No user session; don't fetch.
          if (!mounted) return;
          setProgress(null);
          setDrills([]);
          setError(null);
          return;
        }

        const progressData = await getGolferProgress(golferId);
        if (!mounted) return;
        setProgress(progressData);

      } catch (e) {
        if (!mounted) return;
        setError('Could not load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [golferId]);

  // Transform progress data for ProgressStats component
  const progressStatsData = progress ? {
    totalAnalyses: progress.totalAnalyses,
    last30dAnalyses: progress.last30Days,
    lastAnalysisAt: progress.lastRun?.createdAt,
  } : null;

  // Transform last run data for LastRunCard component
  const lastRunData = progress?.lastRun ? {
    id: progress.lastRun.runId,
    createdAt: progress.lastRun.createdAt,
    topTags: progress.lastRun.flaws,
  } : null;

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="px-6 pt-6 space-y-6">
        {!golferId && (
          <div className="text-sm text-muted-foreground">No user session found.</div>
        )}
        <Card>
          <CardHeader><CardTitle>Progress</CardTitle></CardHeader>
          <CardContent data-testid="progress-section">
            {loading
              ? 'Loading…'
              : error
                ? <div className="text-sm text-red-600">{error}</div>
                : <ProgressStats data={progressStatsData} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Last run</CardTitle></CardHeader>
          <CardContent data-testid="last-run-section">
            {loading
              ? 'Loading…'
              : error
                ? <div className="text-sm text-red-600">{error}</div>
                : <LastRunCard data={lastRunData} />}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
